import {
    type CSSProperties,
    type MouseEvent,
    useCallback,
    useEffect,
    useState,
    useMemo,
    useRef
} from "react";
import {
    StyleSheet,
    View
} from "react-native";
import {
    regulatorKnobSvg,
    CANISTER_DEGREES,
    ALIGNED_NOTCHES,
    gaugeNeedleSvg,
    turretFaceSvg,
    LIQUID_BOTTOM,
    knobFacesSvg,
    logoNeonSvg,
    logoCoreSvg,
    BUBBLES_SVG,
    selectorSvg,
    VIEW_HEIGHT,
    type PipeArt,
    LIQUID_TOP,
    VIEW_WIDTH,
    liquidSvg,
    EXTRA_ART,
    PEDESTALS,
    PLANT_ART,
    pourSvg,
    SCREENS,
    PIPES
} from "./geometry";
import {
    controlHeightOf,
    densityOnDevice,
    type PlantState,
    screenTokensOf,
    type PlantMode,
    PALETTE_NAMES,
    SHARPNESSES,
    REST_STATE,
    DENSITIES,
    primaryOf,
    readoutOf,
    MODES
} from "./model";
import {
    screenMarkupOf
} from "./screens";
import {
    NCoreUIKitTheme,
    ToggleGroup,
    Card,
    Text
} from "ncore-ui-kit";
import {
    useDocsI18n
} from "../../i18n";
import {
    asksForStillness,
    type Spring,
    useInView,
    useLoop,
    spring
} from "../../iso/live";
import {
    ISO_CSS
} from "../../iso/isoKit";

const PLANT_CSS = `${ISO_CSS.replace(".iso[data-theme=\"light\"]", ".plant-root.iso[data-scene=\"light\"]")}
.plant-root .iso-svg [data-part] { cursor: pointer; }
.plant-root .plant-dye .iso-fill { fill: var(--dye); }
.plant-root .plant-dye .iso-top { fill: color-mix(in oklab, var(--dye) 82%, white); }
.plant-root .plant-dye .iso-shade[data-shade="0"] { fill: color-mix(in oklab, var(--dye) 70%, black); }
.plant-root .plant-dye .iso-shade[data-shade="1"] { fill: color-mix(in oklab, var(--dye) 80%, black); }
.plant-root .plant-dye .iso-shade[data-shade="2"] { fill: color-mix(in oklab, var(--dye) 90%, black); }
.plant-root .plant-dye .iso-shade[data-shade="3"] { fill: var(--dye); }
.plant-root .plant-feed .tb-body { fill: var(--dye); }
.plant-root .plant-glass { fill: var(--anatomy-paper); }
.plant-root .plant-core { fill: none; stroke-linecap: butt; stroke-linejoin: round; vector-effect: none; }
.plant-root .plant-core-old { stroke: var(--plant-dye-old); }
.plant-root .plant-old { --dye: var(--plant-dye-old); }
.plant-root .plant-new { --dye: var(--plant-dye-new); }
.plant-root .plant-glass-body .iso-fill, .plant-root .plant-glass-body .iso-top { fill: var(--anatomy-paper); }
.plant-root .plant-glass-body .iso-shade { fill: var(--anatomy-paper); }
.plant-root .plant-glass-body > .iso-edge { stroke: none; }
.plant-root .plant-liquid > .iso-edge { stroke: color-mix(in oklab, var(--dye) 70%, black); }
.plant-root .plant-liquid > .iso-crease { stroke: color-mix(in oklab, var(--dye) 55%, white); }
.plant-root .plant-stream { fill: none; stroke: var(--plant-dye-new); stroke-width: 2.4; stroke-linecap: round; vector-effect: none; }
.plant-root .plant-ripple { fill: none; stroke: color-mix(in oklab, var(--plant-dye-new) 60%, white); stroke-width: 0.8; vector-effect: none; }
.plant-root .plant-bubble { fill: #ffffff; opacity: 0; animation: plant-bubble 3200ms ease-in infinite; }
@keyframes plant-bubble { 0% { opacity: 0; transform: translateY(0); } 15% { opacity: 0.75; } 90% { opacity: 0.6; } 100% { opacity: 0; transform: translateY(var(--rise)); } }
.plant-root .plant-glint { stroke: #ffffff; stroke-opacity: 0.7; stroke-width: 1.4; }
.plant-root .plant-shine { fill: #ffffff; opacity: 0.55; }
.plant-root .plant-flow { fill: none; stroke: #ffffff; stroke-opacity: 0.75; stroke-linecap: round; stroke-dasharray: 0.01 7; vector-effect: none; animation: plant-flow 1400ms linear infinite; }
@keyframes plant-flow { to { stroke-dashoffset: -14; } }
.plant-root .plant-caret { animation: plant-caret 1100ms steps(1, end) infinite; }
@keyframes plant-caret { 50% { opacity: 0; } }
@media (prefers-reduced-motion: reduce) { .plant-root .plant-bubble, .plant-root .plant-flow, .plant-root .plant-caret { animation: none; } }
.plant-root .plant-core-new { stroke: var(--plant-dye-new); }
.plant-root .plant-ui * { vector-effect: none; }
.plant-root .plant-touch { opacity: 0.22; transform-box: fill-box; transform-origin: center; animation: plant-touch 2400ms ease-out infinite; }
@keyframes plant-touch { 0% { opacity: 0.32; transform: scale(0.4); } 60% { opacity: 0.12; transform: scale(1.15); } 100% { opacity: 0; transform: scale(1.3); } }
@media (prefers-reduced-motion: reduce) { .plant-root .plant-touch { animation: none; } }
.plant-root .plant-window { fill: var(--anatomy-shade-0); stroke: var(--anatomy-lo); stroke-width: 0.5px; }
.plant-root .plant-window[data-active] { fill: var(--anatomy-paper); stroke: var(--anatomy-lit); }
.plant-root .plant-stud { fill: var(--anatomy-dot); opacity: 0.45; }
.plant-root .plant-hub { fill: var(--anatomy-paper); stroke: var(--anatomy-mid); stroke-width: 0.6px; }
.plant-root .plant-board-screw { fill: var(--anatomy-dot); opacity: 0.45; }
.plant-root .plant-board-label { fill: var(--anatomy-mid); font-family: var(--anatomy-mono); font-size: 3.2px; letter-spacing: 0.32em; }
.plant-root .plant-notch-edge { fill: none; stroke: var(--anatomy-mid); stroke-width: 0.5px; }
.plant-root .plant-knob-face { fill: var(--anatomy-hi); }
.plant-root .plant-neon { opacity: 0; transition: opacity 200ms ease; filter: drop-shadow(0 0 0.8px var(--plant-neon)) drop-shadow(0 0 2.2px var(--plant-neon)); }
.plant-root .plant-neon[data-on] { opacity: 1; animation: plant-neon-on 1000ms steps(1, end) 1; }
.plant-root .plant-neon-tube { fill: none; stroke: var(--plant-neon); stroke-width: 1.3; stroke-linecap: round; stroke-linejoin: round; vector-effect: none; }
.plant-root .plant-neon-core { fill: none; stroke: color-mix(in oklab, var(--plant-neon) 30%, white); stroke-width: 0.45; stroke-linecap: round; stroke-linejoin: round; vector-effect: none; }
@keyframes plant-neon-on { 0% { opacity: 0.9; } 7% { opacity: 0.1; } 14% { opacity: 1; } 22% { opacity: 0.15; } 30% { opacity: 1; } 58% { opacity: 0.6; } 62% { opacity: 1; } 100% { opacity: 1; } }
@media (prefers-reduced-motion: reduce) { .plant-root .plant-neon[data-on] { animation: none; } }
.plant-root .plant-logo-core { fill: none; stroke: var(--plant-dye-old); stroke-width: 1.4; stroke-linecap: round; vector-effect: none; }
`;

const stylesheet = StyleSheet.create({
    header: {
        justifyContent: "space-between",
        flexDirection: "row",
        flexWrap: "wrap"
    },
    figure: {
        flexDirection: "column"
    },
    group: {
        alignItems: "center",
        flexDirection: "row",
        flexWrap: "wrap"
    }
});

const FLOW_SECONDS = 2.2;

const POUR_SHARE = 0.4;

const TRUNK_SHARE = 0.55;

const TOUR_STEP_MS = 3200;

const TOUR_REST_MS = 9000;

const SELECTOR_STIFFNESS = 60;

const SELECTOR_DAMPING = 11;

const TURRET_STEP = 120;

const SHARPNESS_INDEX = {
    rounded: 2,
    sharp: 0,
    soft: 1
};

const NOTCH_STIFFNESS = 26;

const NOTCH_DAMPING = 7;

const NOTCH_FADE = 40;

const NEON_TOLERANCE = 1.5;

const LOGO_REST_MS = 1400;

const SCATTER_OFFSETS = [
    140,
    -105,
    165,
    -150,
    85,
    -125,
    110
];

const DENSITY_DEGREES = {
    compact: -50,
    spacious: 40
};

const TOUR: Array<Partial<PlantState>> = [
    {
        palette: 1
    },
    {
        mode: "dark"
    },
    {
        density: "compact"
    },
    {
        sharpness: "rounded"
    },
    {
        palette: 2
    },
    {
        mode: "light"
    },
    {
        sharpness: "sharp"
    },
    {
        density: "spacious"
    },
    {
        palette: 0
    },
    {
        sharpness: "soft"
    }
];

const clampUnit = (value: number) => Math.min(Math.max(value, 0), 1);

const smoothStep = (value: number) => {
    const t = clampUnit(value);

    return t * t * (3 - 2 * t);
};

type PipeKey = keyof typeof PIPES;

const PIPE_KEYS: Array<PipeKey> = [
    "main",
    "feed",
    "phone",
    "monitor"
];

const TRUNK_LENGTH = PIPES.main.length + PIPES.feed.length;

/**
 * How far the new dye has run along each pipe at a moment of the flow.
 * The trunk fills in order, then both branches fill together: they are cut to one length.
 */
const pipeShareOf = (key: PipeKey, flow: number) => {
    const trunk = clampUnit((flow - POUR_SHARE) / (TRUNK_SHARE - POUR_SHARE)) * TRUNK_LENGTH;

    if(key === "main") {
        return clampUnit(trunk / PIPES.main.length);
    }

    if(key === "feed") {
        return clampUnit((trunk - PIPES.main.length) / PIPES.feed.length);
    }

    return smoothStep((flow - TRUNK_SHARE) / (1 - TRUNK_SHARE));
};

const PipeView = ({
    coreRef,
    pipe
}: {
    coreRef: (node: SVGPathElement | null) => void;
    pipe: PipeArt;
}) => <g>
    <g
        dangerouslySetInnerHTML={{
            __html: pipe.under
        }}
    />
    <path
        className="plant-core plant-core-old"
        strokeWidth={pipe.width}
        d={pipe.core}
    />
    <path
        strokeDasharray={`${pipe.length} ${pipe.length}`}
        className="plant-core plant-core-new"
        strokeDashoffset={pipe.length}
        strokeWidth={pipe.width}
        ref={coreRef}
        d={pipe.core}
    />
    <path
        strokeWidth={pipe.width * 0.42}
        className="plant-flow"
        d={pipe.core}
    />
    <g
        dangerouslySetInnerHTML={{
            __html: pipe.over
        }}
    />
</g>;

const TokenPlant = () => {
    const {
        shapeConfigs,
        spaces,
        colors
    } = NCoreUIKitTheme.useContext();

    const {
        t
    } = useDocsI18n();

    const [
        state,
        setState
    ] = useState<PlantState>(REST_STATE);

    const [
        arrivedPalette,
        setArrivedPalette
    ] = useState(REST_STATE.palette);

    const [
        flowFrom,
        setFlowFrom
    ] = useState(REST_STATE.palette);

    const rootRef = useRef<HTMLElement>(null);

    const figureRef = useRef<HTMLDivElement>(null);

    const selectorRef = useRef<SVGGElement>(null);

    const knobRef = useRef<SVGGElement>(null);

    const liquidRef = useRef<SVGGElement>(null);

    const turretRef = useRef<SVGGElement>(null);

    const logoKnobsRef = useRef<SVGGElement>(null);

    const logoCoreRef = useRef<SVGGElement>(null);

    const logoNeonRef = useRef<SVGGElement>(null);

    const notchSprings = useRef<Array<Spring>>(ALIGNED_NOTCHES.map((aligned, index) => ({
        at: aligned + SCATTER_OFFSETS[index]!,
        rate: 0
    })));

    const notchTargets = useRef([...ALIGNED_NOTCHES]);

    const scatterCount = useRef(0);

    const needleRefs = useRef<Partial<Record<"phone" | "monitor", SVGGElement | null>>>({});

    const coreRefs = useRef<Partial<Record<PipeKey, SVGPathElement | null>>>({});

    const selectorSpring = useRef<Spring>({
        at: CANISTER_DEGREES[REST_STATE.palette]!,
        rate: 0
    });

    const knobSpring = useRef<Spring>({
        at: DENSITY_DEGREES[REST_STATE.density],
        rate: 0
    });

    const turretSpring = useRef<Spring>({
        at: -SHARPNESS_INDEX[REST_STATE.sharpness] * TURRET_STEP,
        rate: 0
    });

    const needleSprings = useRef({
        monitor: {
            at: controlHeightOf(densityOnDevice("monitor", REST_STATE.density)),
            rate: 0
        },
        phone: {
            at: controlHeightOf(densityOnDevice("phone", REST_STATE.density)),
            rate: 0
        }
    });

    const flow = useRef(1);

    const targets = useRef(REST_STATE);

    const pendingPalette = useRef(REST_STATE.palette);

    const lastTouch = useRef(0);

    const tourStep = useRef(0);

    const inView = useInView(figureRef);

    const [
        pageTheme,
        setPageTheme
    ] = useState<PlantMode>("light");

    const handleDrawFlow = useCallback((share: number) => {
        PIPE_KEYS.forEach((key) => {
            const node = coreRefs.current[key];

            if(node) {
                node.style.strokeDashoffset = String(PIPES[key].length * (1 - pipeShareOf(key, share)));
            }
        });

        const liquid = liquidRef.current;

        if(liquid) {
            liquid.innerHTML = share > 0 && share < 1 ? pourSvg(clampUnit(share / POUR_SHARE)) : "";
        }
    }, []);

    const kick = useLoop((dt, calm) => {
        const selectorState = selectorSpring.current;
        const knobState = knobSpring.current;

        const selectorTarget = CANISTER_DEGREES[targets.current.palette]!;

        const knobTarget = DENSITY_DEGREES[targets.current.density];

        const wasFlowing = flow.current < 1;

        if(calm) {
            selectorState.at = selectorTarget;
            knobState.at = knobTarget;
            flow.current = 1;
        }

        const isSelectorSettled = spring(selectorState, selectorTarget, dt, SELECTOR_STIFFNESS, SELECTOR_DAMPING, calm);

        const isKnobSettled = spring(knobState, knobTarget, dt, SELECTOR_STIFFNESS, SELECTOR_DAMPING, calm);

        const turretTarget = -SHARPNESS_INDEX[targets.current.sharpness] * TURRET_STEP;

        const isTurretSettled = spring(turretSpring.current, turretTarget, dt, SELECTOR_STIFFNESS, SELECTOR_DAMPING, calm);

        if(turretRef.current) {
            turretRef.current.innerHTML = turretFaceSvg(turretSpring.current.at, SHARPNESS_INDEX[targets.current.sharpness]);
        }

        const areNeedlesSettled = ([
            "phone",
            "monitor"
        ] as const).map((device) => {
            const target = controlHeightOf(densityOnDevice(device, targets.current.density));

            const isSettled = spring(needleSprings.current[device], target, dt, SELECTOR_STIFFNESS, SELECTOR_DAMPING, calm);

            const node = needleRefs.current[device];

            if(node) {
                node.innerHTML = gaugeNeedleSvg(device, needleSprings.current[device].at);
            }

            return isSettled;
        }).every(Boolean);

        if(selectorRef.current) {
            selectorRef.current.innerHTML = selectorSvg(selectorState.at);
        }

        if(knobRef.current) {
            knobRef.current.innerHTML = regulatorKnobSvg(knobState.at);
        }

        const areNotchesSettled = notchSprings.current.map((notch, index) => {
            if(calm) {
                notch.at = notchTargets.current[index]!;
            }

            return spring(notch, notchTargets.current[index]!, dt, NOTCH_STIFFNESS + index * 6, NOTCH_DAMPING, calm);
        }).every(Boolean);

        if(logoKnobsRef.current) {
            logoKnobsRef.current.innerHTML = knobFacesSvg(notchSprings.current.map((notch) => notch.at));
        }

        if(logoCoreRef.current) {
            const drift = Math.max(...notchSprings.current.map((notch, index) => Math.abs(notch.at - ALIGNED_NOTCHES[index]!)));

            logoCoreRef.current.style.opacity = String(Math.max(0.12, 1 - drift / NOTCH_FADE));

            const isLit = drift < NEON_TOLERANCE;

            const neon = logoNeonRef.current;

            if(neon && neon.hasAttribute("data-on") !== isLit) {
                neon.toggleAttribute("data-on", isLit);
            }
        }

        if(wasFlowing) {
            flow.current = Math.min(1, flow.current + dt / FLOW_SECONDS);

            handleDrawFlow(flow.current);

            if(flow.current >= 1) {
                setArrivedPalette(pendingPalette.current);

                setFlowFrom(pendingPalette.current);

                notchTargets.current = [...ALIGNED_NOTCHES];
            }
        }

        return !(isSelectorSettled && isKnobSettled && isTurretSettled && areNeedlesSettled && areNotchesSettled) || flow.current < 1;
    }, false);

    useEffect(() => {
        if(flow.current >= 1) {
            handleDrawFlow(0);
        }
    }, [
        flowFrom,
        handleDrawFlow
    ]);

    const handleScatter = useCallback(() => {
        scatterCount.current += 1;

        notchTargets.current = ALIGNED_NOTCHES.map((aligned, index) => aligned + SCATTER_OFFSETS[(index + scatterCount.current) % SCATTER_OFFSETS.length]!);
    }, []);

    const handleSelect = useCallback((change: Partial<PlantState>) => {
        const next = {
            ...targets.current,
            ...change
        };

        if(change.palette !== undefined && change.palette !== pendingPalette.current) {
            pendingPalette.current = change.palette;

            flow.current = 0;

            handleScatter();
        }

        targets.current = next;

        setState(next);

        kick();
    }, [
        handleScatter,
        kick
    ]);

    const handleTouch = useCallback((change: Partial<PlantState>) => {
        lastTouch.current = Date.now();

        handleSelect(change);
    }, [handleSelect]);

    useEffect(() => {
        const root = document.documentElement;

        const handleFollowPage = () => {
            const mode = root.getAttribute("data-theme") === "dark" ? "dark" : "light";

            setPageTheme(mode);

            if(mode !== targets.current.mode) {
                handleSelect({
                    mode
                });
            }
        };

        handleFollowPage();

        const observer = new MutationObserver(handleFollowPage);

        observer.observe(root, {
            attributeFilter: [
                "data-theme"
            ],
            attributes: true
        });

        return () => observer.disconnect();
    }, [handleSelect]);

    useEffect(() => {
        if(!inView || asksForStillness()) {
            return;
        }

        const timer = window.setInterval(() => {
            if(Date.now() - lastTouch.current < TOUR_REST_MS) {
                return;
            }

            handleSelect(TOUR[tourStep.current % TOUR.length]!);

            tourStep.current += 1;
        }, TOUR_STEP_MS);

        return () => window.clearInterval(timer);
    }, [
        inView,
        handleSelect
    ]);

    const onFigureClick = (event: MouseEvent<HTMLDivElement>) => {
        const part = (event.target as Element).closest("[data-part]")?.getAttribute("data-part");

        if(part === "selector") {
            handleTouch({
                palette: (targets.current.palette + 1) % PALETTE_NAMES.length
            });
        }

        if(part === "turret") {
            handleTouch({
                sharpness: SHARPNESSES[(SHARPNESS_INDEX[targets.current.sharpness] + 1) % SHARPNESSES.length]
            });
        }

        if(part === "logo") {
            lastTouch.current = Date.now();

            handleScatter();

            kick();

            window.setTimeout(() => {
                if(flow.current >= 1) {
                    notchTargets.current = [...ALIGNED_NOTCHES];

                    kick();
                }
            }, LOGO_REST_MS);
        }

        if(part === "regulator") {
            handleTouch({
                density: targets.current.density === "compact" ? "spacious" : "compact"
            });
        }
    };

    const screens = useMemo(() => ({
        monitor: screenMarkupOf("monitor", screenTokensOf(state, "monitor", arrivedPalette)),
        phone: screenMarkupOf("phone", screenTokensOf(state, "phone", arrivedPalette))
    }), [
        state,
        arrivedPalette
    ]);

    const dyeStyle = {
        "--plant-dye-new": primaryOf(pendingPalette.current, state.mode),
        "--plant-dye-old": primaryOf(flowFrom, state.mode),
        "--plant-neon": primaryOf(arrivedPalette, "dark"),
        "--anatomy-paper": colors.content.container.mid,
        maxWidth: shapeConfigs.contentMaxWidth,
        ...Object.fromEntries(PALETTE_NAMES.map((_, index) => [
            `--plant-canister-${index}`,
            primaryOf(index, state.mode)
        ]))
    } as CSSProperties;

    const restSelector = useMemo(() => selectorSvg(CANISTER_DEGREES[REST_STATE.palette]!), []);

    const restKnob = useMemo(() => regulatorKnobSvg(DENSITY_DEGREES[REST_STATE.density]), []);

    const restTurret = useMemo(() => turretFaceSvg(-SHARPNESS_INDEX[REST_STATE.sharpness] * TURRET_STEP, SHARPNESS_INDEX[REST_STATE.sharpness]), []);

    const restNeedles = useMemo(() => ({
        monitor: gaugeNeedleSvg("monitor", controlHeightOf(densityOnDevice("monitor", REST_STATE.density))),
        phone: gaugeNeedleSvg("phone", controlHeightOf(densityOnDevice("phone", REST_STATE.density)))
    }), []);

    const restKnobFaces = useMemo(() => knobFacesSvg(ALIGNED_NOTCHES.map((aligned, index) => aligned + SCATTER_OFFSETS[index]!)), []);

    useEffect(() => {
        if(inView) {
            kick();
        }
    }, [
        inView,
        kick
    ]);

    return <section
        className="plant-root iso"
        data-scene={pageTheme}
        style={dyeStyle}
        ref={rootRef}
    >
        <style>
            {PLANT_CSS}
        </style>
        <View
            style={[
                stylesheet.figure,
                {
                    gap: spaces.spacingSm
                }
            ]}
            role="figure"
        >
            <Card
                style={{
                    backgroundColor: colors.content.container.mid
                }}
                spreadBehaviour="stretch"
                variant="outline"
            >
                <Card.Content>
                    <View
                        style={[
                            stylesheet.header,
                            {
                                gap: spaces.spacingMd
                            }
                        ]}
                    >
                        <Text
                            variant="labelSmallSize"
                            color="low"
                        >
                            {t("intro.plantFig")}
                        </Text>
                        <Text
                            variant="labelSmallSize"
                            color="low"
                        >
                            {t("intro.plantTitle")}
                        </Text>
                    </View>
                    <div
                        style={{
                            padding: `${spaces.spacingXl}px 0`
                        }}
                        aria-label={t("intro.plantLabel")}
                        onClick={onFigureClick}
                        ref={figureRef}
                        role="group"
                    >
                        <svg
                            viewBox={`0 0 ${VIEW_WIDTH} ${VIEW_HEIGHT}`}
                            className="iso-svg"
                            aria-hidden="true"
                            focusable="false"
                        >
                            <defs>
                                <clipPath
                                    dangerouslySetInnerHTML={{
                                        __html: SCREENS.monitor.clip
                                    }}
                                    id="plant-clip-monitor"
                                />
                                <clipPath
                                    dangerouslySetInnerHTML={{
                                        __html: SCREENS.phone.clip
                                    }}
                                    id="plant-clip-phone"
                                />
                            </defs>
                            <g
                                dangerouslySetInnerHTML={{
                                    __html: PLANT_ART.base
                                }}
                            />
                            <g
                                dangerouslySetInnerHTML={{
                                    __html: EXTRA_ART.label + PLANT_ART.tank + EXTRA_ART.turret
                                }}
                            />
                            <g
                                dangerouslySetInnerHTML={{
                                    __html: restTurret
                                }}
                                data-part="turret"
                                ref={turretRef}
                            />
                            <g
                                dangerouslySetInnerHTML={{
                                    __html: PLANT_ART.glassBack
                                }}
                            />
                            <g
                                dangerouslySetInnerHTML={{
                                    __html: liquidSvg(LIQUID_BOTTOM, LIQUID_TOP)
                                }}
                                className="plant-old"
                            />
                            <g
                                className="plant-new"
                                ref={liquidRef}
                            />
                            <g
                                dangerouslySetInnerHTML={{
                                    __html: BUBBLES_SVG
                                }}
                            />
                            <g
                                dangerouslySetInnerHTML={{
                                    __html: PLANT_ART.glassFront + PLANT_ART.lid + PLANT_ART.canisters
                                }}
                            />
                            <g
                                dangerouslySetInnerHTML={{
                                    __html: restSelector
                                }}
                                data-part="selector"
                                ref={selectorRef}
                            />
                            <g
                                dangerouslySetInnerHTML={{
                                    __html: PLANT_ART.monitor
                                }}
                            />
                            <g
                                clipPath="url(#plant-clip-monitor)"
                            >
                                <g
                                    transform={SCREENS.monitor.transform}
                                    dangerouslySetInnerHTML={{
                                        __html: screens.monitor
                                    }}
                                    className="plant-ui"
                                />
                            </g>
                            <g
                                dangerouslySetInnerHTML={{
                                    __html: SCREENS.monitor.outline + PLANT_ART.phone
                                }}
                            />
                            <g
                                clipPath="url(#plant-clip-phone)"
                            >
                                <g
                                    transform={SCREENS.phone.transform}
                                    dangerouslySetInnerHTML={{
                                        __html: screens.phone
                                    }}
                                    className="plant-ui"
                                />
                            </g>
                            <g
                                dangerouslySetInnerHTML={{
                                    __html: SCREENS.phone.outline + PLANT_ART.phoneLip
                                }}
                            />
                            <PipeView
                                coreRef={(node) => {
                                    coreRefs.current.main = node;
                                }}
                                pipe={PIPES.main}
                            />
                            <g
                                dangerouslySetInnerHTML={{
                                    __html: PLANT_ART.regulator
                                }}
                            />
                            <g
                                dangerouslySetInnerHTML={{
                                    __html: restKnob
                                }}
                                data-part="regulator"
                                ref={knobRef}
                            />
                            <PipeView
                                coreRef={(node) => {
                                    coreRefs.current.feed = node;
                                }}
                                pipe={PIPES.feed}
                            />
                            <g
                                dangerouslySetInnerHTML={{
                                    __html: PLANT_ART.tee + PEDESTALS.phone
                                }}
                            />
                            <PipeView
                                coreRef={(node) => {
                                    coreRefs.current.phone = node;
                                }}
                                pipe={PIPES.phone}
                            />
                            <g
                                dangerouslySetInnerHTML={{
                                    __html: EXTRA_ART.checkValve + EXTRA_ART.phoneGauge
                                }}
                            />
                            <g
                                ref={(node) => {
                                    needleRefs.current.phone = node;
                                }}
                                dangerouslySetInnerHTML={{
                                    __html: restNeedles.phone
                                }}
                            />
                            <g
                                dangerouslySetInnerHTML={{
                                    __html: PEDESTALS.monitor
                                }}
                            />
                            <PipeView
                                coreRef={(node) => {
                                    coreRefs.current.monitor = node;
                                }}
                                pipe={PIPES.monitor}
                            />
                            <g
                                dangerouslySetInnerHTML={{
                                    __html: EXTRA_ART.monitorGauge
                                }}
                            />
                            <g
                                ref={(node) => {
                                    needleRefs.current.monitor = node;
                                }}
                                dangerouslySetInnerHTML={{
                                    __html: restNeedles.monitor
                                }}
                            />
                            <g
                                data-part="logo"
                            >
                                <g
                                    dangerouslySetInnerHTML={{
                                        __html: EXTRA_ART.logoBoard
                                    }}
                                />
                                <g
                                    dangerouslySetInnerHTML={{
                                        __html: logoCoreSvg()
                                    }}
                                    ref={logoCoreRef}
                                />
                                <g
                                    dangerouslySetInnerHTML={{
                                        __html: restKnobFaces
                                    }}
                                    ref={logoKnobsRef}
                                />
                                <g
                                    dangerouslySetInnerHTML={{
                                        __html: logoNeonSvg()
                                    }}
                                    className="plant-neon"
                                    ref={logoNeonRef}
                                />
                            </g>
                        </svg>
                    </div>
                </Card.Content>
                <Card.Footer>
                    <View
                        style={[
                            stylesheet.group,
                            {
                                gap: spaces.spacingSm
                            }
                        ]}
                    >
                        <Text
                            variant="labelSmallSize"
                            color="low"
                        >
                            {t("intro.plantPalette")}
                        </Text>
                        <ToggleGroup
                            onValueChange={(value) => handleTouch({
                                palette: Number(value)
                            })}
                            title={t("intro.plantPalette")}
                            value={String(state.palette)}
                            attachBehaviour="attached"
                            isClearEnabled={false}
                            variant="outline"
                            size="small"
                        >
                            {PALETTE_NAMES.map((item, index) => <ToggleGroup.Item
                                value={String(index)}
                                title={item}
                                key={item}
                            />)}
                        </ToggleGroup>
                    </View>
                    <View
                        style={[
                            stylesheet.group,
                            {
                                gap: spaces.spacingSm
                            }
                        ]}
                    >
                        <Text
                            variant="labelSmallSize"
                            color="low"
                        >
                            {t("intro.plantMode")}
                        </Text>
                        <ToggleGroup
                            onValueChange={(value) => handleTouch({
                                mode: value as PlantMode
                            })}
                            title={t("intro.plantMode")}
                            value={String(state.mode)}
                            attachBehaviour="attached"
                            isClearEnabled={false}
                            variant="outline"
                            size="small"
                        >
                            {MODES.map((item) => <ToggleGroup.Item
                                value={String(item)}
                                title={item}
                                key={item}
                            />)}
                        </ToggleGroup>
                    </View>
                    <View
                        style={[
                            stylesheet.group,
                            {
                                gap: spaces.spacingSm
                            }
                        ]}
                    >
                        <Text
                            variant="labelSmallSize"
                            color="low"
                        >
                            {t("intro.plantDensity")}
                        </Text>
                        <ToggleGroup
                            onValueChange={(value) => handleTouch({
                                density: value as PlantState["density"]
                            })}
                            title={t("intro.plantDensity")}
                            value={String(state.density)}
                            attachBehaviour="attached"
                            isClearEnabled={false}
                            variant="outline"
                            size="small"
                        >
                            {DENSITIES.map((item) => <ToggleGroup.Item
                                value={String(item)}
                                title={item}
                                key={item}
                            />)}
                        </ToggleGroup>
                    </View>
                    <View
                        style={[
                            stylesheet.group,
                            {
                                gap: spaces.spacingSm
                            }
                        ]}
                    >
                        <Text
                            variant="labelSmallSize"
                            color="low"
                        >
                            {t("intro.plantSharpness")}
                        </Text>
                        <ToggleGroup
                            onValueChange={(value) => handleTouch({
                                sharpness: value as PlantState["sharpness"]
                            })}
                            title={t("intro.plantSharpness")}
                            value={String(state.sharpness)}
                            attachBehaviour="attached"
                            isClearEnabled={false}
                            variant="outline"
                            size="small"
                        >
                            {SHARPNESSES.map((item) => <ToggleGroup.Item
                                value={String(item)}
                                title={item}
                                key={item}
                            />)}
                        </ToggleGroup>
                    </View>
                    <Text
                        variant="labelSmallSize"
                        color="low"
                    >
                        {readoutOf(state)}
                    </Text>
                </Card.Footer>
            </Card>
            <Text
                variant="bodyMediumSize"
                color="mid"
            >
                {t("intro.plantCaption")}
            </Text>
        </View>
    </section>;
};
export default TokenPlant;
